import client from './client';
import type { ColumnState } from 'ag-grid-community';

export interface PowerPlantTag {
  id: number;
  label: string;
  color: string;
}

export interface PowerPlantMapPreferences {
  basemap?: string;
  clusterLabelMode?: 'hidden' | 'visible';
  weatherOverlay?: 'none' | 'temperature' | 'precipitation' | 'wind' | 'radar' | 'clouds';
  activeUuid?: string | null;
}

export interface PowerPlantTableColumnState extends ColumnState {
  colId: string;
}

export interface PowerPlantTablePreferences {
  columnState?: PowerPlantTableColumnState[];
  cardOrder?: string[];
  cardMetricOrder?: string[];
}

export interface PowerPlantForecastSolarDailyItem {
  dailyForecastKwh: number | null;
  fetchedAt: string;
  source: 'forecast.solar' | null;
}

export interface PowerPlant {
  power_plant_id: number;
  power_plant_uuid_id: string;
  power_plant_name: string;
  power_plant_town: string;
  power_plant_city: string;
  power_plant_lat: number | null;
  power_plant_lon: number | null;
  power_plant_investor_name: string;
  power_plant_investor_uuid_id?: string | null;
  power_plant_management_company_name?: string | null;
  power_plant_ac_dc_ratio?: number | null;
  power_plant_e_day: string;
  power_plant_e_day_kwh?: number;
  power_plant_e_week_kwh?: number;
  power_plant_e_month_kwh?: number;
  power_plant_e_year_kwh?: number;
  power_plant_e_total_kwh?: number;
  power_plant_e_week?: string;
  power_plant_e_month?: string;
  power_plant_e_year?: string;
  power_plant_forecast_month?: string | null;
  power_plant_forecast_total?: string | null;
  power_plant_forecast_source?: 'manual' | 'pvgis' | null;
  power_plant_e_total: string;
  power_plant_pac_now: number | null;
  power_plant_ia_now?: number | null;
  power_plant_ib_now?: number | null;
  power_plant_ic_now?: number | null;
  power_plant_qac_now?: number | null;
  power_plant_sac_now?: number | null;
  power_plant_pf_now?: number | null;
  power_plant_frequency_now?: number | null;
  power_plant_van_now?: number | null;
  power_plant_vbn_now?: number | null;
  power_plant_vcn_now?: number | null;
  power_plant_vab_now?: number | null;
  power_plant_vbc_now?: number | null;
  power_plant_vca_now?: number | null;
  power_plant_gti_now?: number | null;
  power_plant_wpneg?: number | null;
  power_plant_wpneg_pm?: number | null;
  power_plant_wppos?: number | null;
  power_plant_wppos_pm?: number | null;
  power_plant_wqneg?: number | null;
  power_plant_wqpos?: number | null;
  power_plant_h04_cb?: string | null;
  power_plant_voltage_now: number | null;
  power_plant_current_now: number | null;
  power_plant_generation_now: number | null;
  power_plant_consumption_now: number | null;
  power_plant_irradiance_now?: number | null;
  has_irradiance?: boolean;
  has_panels?: boolean;
  has_smartlogger?: boolean;
  has_power_meter?: boolean;
  has_sensor?: boolean;
  power_plant_last_seen: string;
  power_plant_status: number;
  maintenance_contract_status: 'active' | 'expiring_soon' | 'none';
  power_plant_tags: PowerPlantTag[];
}

export interface SolarPanelSummary {
  solar_module_brand: string | null;
  solar_module_model: string | null;
  solar_module_max_module_power: number | null;
  solar_module_efficiency: number | null;
  solar_module_temperature_coefficient: number | null;
  module_quantity: number | null;
}

export async function fetchPowerPlants(): Promise<PowerPlant[]> {
  const res = await client.get<PowerPlant[]>('/powerplants');
  return res.data;
}

export async function fetchPowerPlantDetailSolarPanelSummary(uuid_id: string, signal?: AbortSignal): Promise<SolarPanelSummary[]> {
  const res = await client.get<SolarPanelSummary[]>(`/powerplants/${uuid_id}/solar-panels`, { signal });
  return res.data ?? [];
}

export async function fetchPowerPlantTagFilter(): Promise<number[]> {
  const res = await client.get<{ items: number[] }>('/powerplants/tag-filter');
  return res.data.items ?? [];
}

export async function savePowerPlantTagFilter(tagIds: number[]): Promise<void> {
  await client.put('/powerplants/tag-filter', { tag_ids: tagIds });
}

export async function fetchPowerPlantMapPreferences(): Promise<PowerPlantMapPreferences> {
  const res = await client.get<{ item: PowerPlantMapPreferences }>('/powerplants/map-preferences');
  return res.data.item ?? {};
}

export async function savePowerPlantMapPreferences(preferences: PowerPlantMapPreferences): Promise<void> {
  await client.put('/powerplants/map-preferences', preferences);
}

export async function fetchPowerPlantTablePreferences(): Promise<PowerPlantTablePreferences> {
  const res = await client.get<{ item: PowerPlantTablePreferences }>('/powerplants/table-preferences');
  return res.data.item ?? {};
}

export async function savePowerPlantTablePreferences(preferences: PowerPlantTablePreferences): Promise<void> {
  await client.put('/powerplants/table-preferences', preferences);
}

export async function clearPowerPlantTablePreferences(): Promise<void> {
  await client.delete('/powerplants/table-preferences');
}

export async function fetchPowerPlantForecastSolarDailyBulk(
  uuidIds: string[],
): Promise<Record<string, PowerPlantForecastSolarDailyItem | null>> {
  const res = await client.post<{ items: Record<string, PowerPlantForecastSolarDailyItem | null> }>(
    '/powerplants/forecast-solar/daily',
    { uuid_ids: uuidIds },
  );
  return res.data.items ?? {};
}

export interface PowerPlantParam {
  param_name: string;
  param_value: string;
}

export interface PowerPlantContactCardEntry {
  name: string;
  phone: string;
}

export type PowerPlantContactCardGroups = Record<string, PowerPlantContactCardEntry[]>;

export interface ManualForecastRow {
  roofOrientation: string;
  months: Array<number | null>;
  total: number;
}

export interface ManualForecastResponse {
  year: number;
  rows: ManualForecastRow[];
}

export async function fetchPowerPlantParams(uuid_id: string): Promise<PowerPlantParam[]> {
  const res = await client.get<PowerPlantParam[]>(`/powerplants/${uuid_id}/params`);
  return res.data;
}

export async function savePowerPlantParams(uuid_id: string, params: PowerPlantParam[]): Promise<void> {
  await client.put(`/powerplants/${uuid_id}/params`, { params });
}

export async function fetchPowerPlantContactCards(uuid_id: string): Promise<PowerPlantContactCardGroups> {
  const res = await client.get<{ groups: PowerPlantContactCardGroups }>(`/powerplants/${uuid_id}/contact-cards`);
  return res.data.groups ?? {};
}

export async function savePowerPlantContactCards(uuid_id: string, groups: PowerPlantContactCardGroups): Promise<void> {
  await client.put(`/powerplants/${uuid_id}/contact-cards`, { groups });
}

export async function fetchPowerPlantManualForecast(uuid_id: string, year: number): Promise<ManualForecastResponse> {
  const res = await client.get<ManualForecastResponse>(`/powerplants/${uuid_id}/manual-forecast`, {
    params: { year },
  });
  return res.data;
}

export async function savePowerPlantManualForecast(
  uuid_id: string,
  payload: { year: number; rows: Array<{ roofOrientation: string; months: Array<number | null> }> },
): Promise<ManualForecastResponse> {
  const res = await client.put<ManualForecastResponse>(`/powerplants/${uuid_id}/manual-forecast`, payload);
  return res.data;
}

export interface PowerPlantAlarmCounts {
  total: number;
  warning: number;
  minor: number;
  major: number;
}

export async function fetchPowerPlantsAlarmCounts(
  plantIds: number[],
): Promise<Record<string, PowerPlantAlarmCounts>> {
  const res = await client.post<Record<string, PowerPlantAlarmCounts>>('/powerplants/alarm-counts', {
    plant_ids: plantIds,
  });
  return res.data;
}
